import { useActiveAddress, useConnection } from "@arweave-wallet-kit/react"
import { css } from "@linaria/core"
import { styled } from "@linaria/react"
import {
  Collapse,
  Dialog,
  Paper,
  Skeleton,
  Stack,
  Tab,
  tabClasses,
  Tabs,
  tabsClasses,
  Link as MuiLink,
  Tooltip,
  Box,
  useMediaQuery,
  Theme,
  Typography,
  CircularProgress,
  DialogContent,
  DialogActions,
} from "@mui/material"
import { useQuery } from "@tanstack/react-query"
import {
  ArrowNarrowRight,
  ChevronDown,
  HelpCircle,
  InfoCircle,
  RefreshCw01,
  XClose,
} from "@untitled-ui/icons-react"

import { motion } from "framer-motion"
import { enqueueSnackbar } from "notistack"
import { useContext, useEffect, useLayoutEffect, useMemo, useState } from "react"

import { useNavigate, useSearchParams } from "react-router-dom"

import { useDeployHopperAgent } from "../ao/agent"
import { cancelHopperOrder, dcaOrder, limitOrder, stopLossOrder } from "../ao/agents-api"
import { useAo } from "../ao/ao"
import { getSuggestedPools } from "../ao/dexi"
import { useGlobalLiquidity } from "../ao/monitor-api"
import {
  readSwapResult,
  getSwapOutput,
  getPriceImpact,
  OrderStatus,
  swap,
  useFeePercentage,
  useReserves,
  wait,
  Reserves,
  swapMultiHop,
  readSwapResultMultiHop,
  simulateMultiHop,
} from "../ao/pool"

import { useSwapRoute, useTokens } from "../ao/router"
import { getBalance, TokenInfo } from "../ao/token"
import { AgentSvg } from "../components/AgentSvg"
import Alert from "../components/Alert"
import Button from "../components/Button"
import { CardholderIcon } from "../components/CardholderIcon"
import { IconButton } from "../components/IconButton"
import { DEFAULT_SLIPPAGE, SettingsPopover } from "../components/SettingsPopover"
import { TitleBar } from "../components/TitleBar"
import TokenInput from "../components/TokenInput"
import {
  CreateAgentStage,
  CreateAgentAnimation,
} from "../components/create-agent-anim/CreateAgentAnimation"
import { MultiHopOrderDetails } from "../components/swap/MultiHopOrderDetails"
import { SwapMultiHopRoute, TokenDetails } from "../components/swap/SwapMultiHopRoute"
import { SwapOrderDetails } from "../components/swap/SwapOrderDetails"
import { SwapOrderPendingForm } from "../components/swap/SwapOrderPendingForm"
import { SwapOrderPreviewForm } from "../components/swap/SwapOrderPreviewForm"
import { SwapOrderSnackbar } from "../components/swap/SwapOrderSnackbar"
import { SwapOrderSuccessForm } from "../components/swap/SwapOrderSuccessForm"
import { DcaOrderForm, DcaOrderFormValues } from "../components/swap/types/DcaOrderForm"
import { LimitOrderForm, LimitOrderFormValues } from "../components/swap/types/LimitOrderForm"
import {
  StopLosOrderFormValues,
  StopLossOrderForm,
} from "../components/swap/types/StopLossOrderForm"
import { BUTTON_LABELS, OrderType } from "../components/swap/types/swap-types"
import { ConfigContext } from "../contexts/config"

import { useSimpleAgent } from "../contexts/simple-agent"
import useHopperPrice from "../hooks/use-hopper-price"
import { useTokenInfo } from "../hooks/use-token-info"
import { HELP_LINK_TOOLTIPS, HELP_LINKS, INPUT_DECIMALS, REFRESH_INTERVAL } from "../settings"
import { NumberFont, TitleFont } from "../theme"
import {
  parseBigIntAsNumber,
  formatTicker,
  parseNumberAsBigInt,
  formatNumberAuto,
  truncateNumberAuto,
} from "../utils/format"
import { createLogger } from "../utils/logger"
import { retryable, showExperimentalFeatures } from "../utils/utils"

const logger = createLogger("SWAP-PAGE")

export default function Swap() {
  useEffect(() => {
    document.title = "Botega"
  }, [])

  const navigate = useNavigate()

  const simpleAgent = useSimpleAgent()

  // inputs
  const [fromValue, setFromValue] = useState<string>("")
  const [toValue, setToValue] = useState<string>("")

  // isMultipleHops
  const [isMultipleHops, setIsMultipleHops] = useState<boolean>(false)

  // token selections
  const [searchParams, setSearchParams] = useSearchParams()
  const fromTokenId = searchParams.get("from") || undefined
  const toTokenId = searchParams.get("to") || undefined
  const orderType = (searchParams.get("type") || "swap") as OrderType

  // ! when default_sell_token_id is to be expected again, comment this back in
  // // if there is no selections at all, set default token as from token
  // useEffect(() => {
  //   if (!fromTokenId && !toTokenId) {
  //     searchParams.set("from", DEFAULT_SELL_TOKEN_ID)
  //     setSearchParams(searchParams, { replace: true })
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [fromTokenId, toTokenId])

  // if from and to tokens are the same, remove to token
  useEffect(() => {
    if (fromTokenId && toTokenId && fromTokenId === toTokenId) {
      searchParams.delete("to")
      setSearchParams(searchParams, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromTokenId, toTokenId])

  // switch tokens from - to
  function switchTokens() {
    if (fromTokenId) searchParams.set("to", fromTokenId)
    else searchParams.delete("to")

    if (toTokenId) searchParams.set("from", toTokenId)
    else searchParams.delete("from")

    setSearchParams(searchParams, { replace: true })
    setFromValue(toValue)
    setToValue("")
  }

  // token infos
  const [fromToken] = useTokenInfo(fromTokenId)
  const [toToken] = useTokenInfo(toTokenId)

  // all tokens
  const [tokens] = useTokens()

  const { data: suggestedPools } = useQuery({
    queryKey: ["suggestedTokens", fromTokenId],
    enabled: !!fromTokenId,
    queryFn: async () => {
      if (!fromTokenId) throw new Error("No from token id")
      const pools = await getSuggestedPools(fromTokenId, ao)
      // console.log("onSuggestedPools", pools)
      return pools
    },
  })

  const suggestedTokens = useMemo(() => {
    if (!suggestedPools || !tokens)
      return tokens?.filter((token) => token.id !== fromTokenId) || null

    const suggestions = tokens
      .map((token) => ({
        ...token,
        poolTvl: suggestedPools.find(
          (pool) => pool.t0_process === token.id || pool.t1_process === token.id,
        )?.tvl_in_usd,
      }))
      .filter((token) => token.id !== fromTokenId)

    // sort by value, otherwise sort by poolTvl
    suggestions.sort((a, b) => {
      if (a.value && !b.value) return -1
      if (!a.value && b.value) return 1
      if (a.value && b.value) return a.value > b.value ? -1 : 1

      return (b.poolTvl || 0) - (a.poolTvl || 0)
    })

    return suggestions
  }, [suggestedPools, tokens, fromTokenId])

  // Check if there is only one hop in the swap route
  const existingSimpleRoute = useMemo(() => {
    if (!suggestedPools || !fromTokenId || !toTokenId) return null

    return suggestedPools.find(
      (pool) =>
        (pool.t0_process === fromTokenId && pool.t1_process === toTokenId) ||
        (pool.t0_process === toTokenId && pool.t1_process === fromTokenId),
    )
  }, [suggestedPools, fromTokenId, toTokenId])

  // find pool
  const { data: swapRoute, isLoading: loadingPool } = useSwapRoute(
    fromTokenId,
    toTokenId,
    Number(fromValue) > 0
      ? parseNumberAsBigInt(fromValue, fromToken?.denomination).toString()
      : undefined,
  )

  // Check if there is only one hop in the swap route
  const poolId =
    Number(fromValue) > 0
      ? existingSimpleRoute
        ? existingSimpleRoute.amm_process
        : swapRoute && swapRoute.dijkstra_route.pool_addresses.length === 1
          ? swapRoute.dijkstra_route.pool_addresses[0]
          : undefined
      : undefined

  useEffect(() => {
    if (poolId) {
      setIsMultipleHops(false)
      return
    }

    if (swapRoute && swapRoute.dijkstra_route.pool_addresses.length > 1) {
      setToValue(parseBigIntAsNumber(swapRoute.dijkstra_route.amount_out, toToken?.denomination))
      setIsMultipleHops(true)
    }
  }, [swapRoute, toToken, poolId])

  // pool data for multi-hops
  const { isLoading: loadingGlobalLiq } = useGlobalLiquidity()

  // reserves
  const [reserves, loadingReserves, refetchReserves] = useReserves(poolId, fromTokenId, toTokenId)

  useEffect(() => {
    if (!poolId) return

    const interval = setInterval(() => {
      setRefresh(Math.random())
      refetchReserves().then(({ data }) => {
        logger.log("onReservesUpdate", data)
      })
    }, REFRESH_INTERVAL)

    return () => {
      clearInterval(interval)
    }
  }, [poolId, refetchReserves])

  // pool fee percentage
  const [feePercentageBps] = useFeePercentage(poolId) // TODO: this is hardcoded atm to 0.25%

  // slippage tolerance percentage
  const [slippage, setSlippage] = useState(DEFAULT_SLIPPAGE)

  // ao instance
  const ao = useAo()

  // config
  const [config] = useContext(ConfigContext)

  // active address
  const activeAddr = useActiveAddress()

  // connect wallet
  const { connect } = useConnection()

  const simpleHopperAddress = simpleAgent?.agentProcess
  const isAgentLocked =
    simpleAgent &&
    simpleAgent.agent &&
    ((simpleAgent.agent["Locked"] as any) === true || simpleAgent.agent["Locked"] === "true")
  const isAgentStuck = simpleAgent && simpleAgent.agent && simpleAgent.agent["IsStuck"] === "true"

  const agentPaths = useMemo(() => {
    if (!isAgentStuck || !simpleAgent || !simpleAgent.agent) return []

    try {
      return JSON.parse(simpleAgent.agent["CurrentPath"]) as string[]
    } catch {
      return []
    }
  }, [simpleAgent, isAgentStuck])

  // normalized input values
  const fromValueNormalized = useMemo<bigint>(() => {
    if (!fromToken) return 0n
    try {
      return parseNumberAsBigInt(fromValue, fromToken.denomination)
    } catch {
      return 0n
    }
  }, [fromValue, fromToken])
  const toValueNormalized = useMemo<bigint>(() => {
    if (!toToken) return 0n
    try {
      return parseNumberAsBigInt(toValue, toToken.denomination)
    } catch {
      return 0n
    }
  }, [toValue, toToken])

  // balances
  const { data: fromBalance, refetch: refetchFromBalance } = useQuery({
    queryKey: [`balance_${fromTokenId}`, activeAddr, config],
    enabled: !!activeAddr && !!fromTokenId,
    queryFn: async () => {
      if (!activeAddr) throw new Error("No active address")
      if (!fromTokenId) throw new Error("No from token id")

      return getBalance(fromTokenId, activeAddr, ao).catch(() => 0n)
    },
  })
  const { data: toBalance, refetch: refetchToBalance } = useQuery({
    queryKey: [`balance_${toTokenId}`, activeAddr, config],
    enabled: !!activeAddr && !!toTokenId,
    queryFn: async () => {
      if (!activeAddr) throw new Error("No active address")
      if (!toTokenId) throw new Error("No to token id")

      return getBalance(toTokenId, activeAddr, ao).catch(() => 0n)
    },
  })

  // loading output qty
  const [loadingOutput, setLoadingOutput] = useState(false)
  const [refresh, setRefresh] = useState(0)

  // listen to changes to the "from" quantity and
  // adjust the "to" quantity with it
  useLayoutEffect(() => {
    if (orderType === "dca") return
    if (orderType === "stop-loss") return
    if (orderType === "limit") return

    const abortController = new AbortController()

    ;(async () => {
      if (loadingPool || !poolId || !toToken) return
      if (!fromValueNormalized || fromValueNormalized === 0n || !fromTokenId || !toTokenId) {
        setToValue("0")
        return
      }

      setLoadingOutput(true)
      let tries = 0

      while (tries < 5) {
        try {
          const zeroAddress = "0000000000000000000000000000000000000000000"
          // get output for token qty
          const output = await getSwapOutput(
            fromTokenId,
            fromValueNormalized,
            poolId,
            activeAddr || zeroAddress,
            ao,
          )

          if (abortController.signal.aborted) return

          logger.log("onSwapQuote", {
            from: fromTokenId,
            to: toTokenId,
            input: fromValueNormalized,
            output,
          })

          // breaks
          if (output !== 0n) {
            const outputAsNumber = parseBigIntAsNumber(output, toToken.denomination, INPUT_DECIMALS)
            setToValue(
              outputAsNumber === "0"
                ? parseBigIntAsNumber(output, toToken.denomination, toToken.denomination)
                : outputAsNumber,
            )
            setLoadingOutput(false)
            return
          }
        } catch {}
        tries++
      }

      setToValue("")
      setLoadingOutput(false)
    })()

    return () => {
      setLoadingOutput(false)
      abortController.abort()
    }
  }, [
    fromTokenId,
    fromToken,
    toToken,
    toTokenId,
    poolId,
    loadingPool,
    ao,
    orderType,
    fromValueNormalized,
    refresh,
    activeAddr,
  ])

  // details dropdown
  const [detailsDropdownOpen, setDetailsDropdownOpen] = useState(false)

  // price impact
  const priceImpact = useMemo(() => {
    if (
      !fromTokenId ||
      !toTokenId ||
      !fromValue ||
      !reserves ||
      !reserves[toTokenId] ||
      !reserves[fromTokenId] ||
      orderType !== "swap"
    ) {
      return "0"
    }

    return getPriceImpact(fromTokenId, fromValueNormalized, reserves)
  }, [fromTokenId, toTokenId, fromValue, reserves, orderType, fromValueNormalized])

  // price
  const [priceForAsset, setPriceForAsset] = useState<"from" | "to">("to")
  const price = useMemo(() => {
    if (
      typeof fromValueNormalized !== "bigint" ||
      typeof toValueNormalized !== "bigint" ||
      toValueNormalized === 0n ||
      !fromTokenId ||
      !toTokenId ||
      !fromToken ||
      !toToken
    )
      return undefined

    if (loadingOutput) return null

    const base = priceForAsset === "from" ? fromValueNormalized : toValueNormalized
    const baseToken = priceForAsset === "from" ? fromToken : toToken
    const quote = priceForAsset === "from" ? toValueNormalized : fromValueNormalized
    const quoteToken = priceForAsset === "from" ? toToken : fromToken
    let price =
      quote === 0n
        ? null
        : Number(quote) /
          10 ** quoteToken.denomination /
          (Number(base) / 10 ** baseToken.denomination)

    logger.log("onPriceUpdate", {
      fromValueNormalized,
      toValueNormalized,
      base,
      quote,
      price,
      quoteToken,
    })

    return price
  }, [
    fromValueNormalized,
    toValueNormalized,
    fromTokenId,
    toTokenId,
    fromToken,
    toToken,
    loadingOutput,
    priceForAsset,
  ])

  // fee quantity
  const fee = useMemo(() => {
    try {
      const fee = (fromValueNormalized * feePercentageBps) / 10_000n
      logger.log("onFeeUpdate", {
        fromValueNormalized,
        feePercentageBps,
        fee,
      })

      return parseBigIntAsNumber(fee, fromToken?.denomination, fromToken?.denomination)
    } catch {
      return ""
    }
  }, [fromValueNormalized, feePercentageBps, fromToken?.denomination])

  // receive at least
  const [receiveAtLeastNormalized, receiveAtLeast] = useMemo<
    [bigint, string | null | undefined]
  >(() => {
    // Check it's a multi hop
    if (isMultipleHops && swapRoute?.dijkstra_route) {
      return [
        BigInt(swapRoute?.dijkstra_route.amount_out),
        parseBigIntAsNumber(
          BigInt(swapRoute?.dijkstra_route.amount_out),
          toToken?.denomination,
          toToken?.denomination,
        ),
      ]
    }

    if (typeof fromValueNormalized !== "bigint" || typeof toValueNormalized !== "bigint")
      return [0n, undefined]

    if (loadingOutput) return [0n, null]

    const slippageBN = BigInt(((100 - slippage) * 1_000_000).toFixed(0))
    const output = toValueNormalized
    const minimumReceived = (output * slippageBN) / 1_000_000n / 100n

    logger.log("onSlippageCalculation", {
      output,
      slippage,
      slippageBN,
      minimumReceived,
    })

    return [
      minimumReceived,
      parseBigIntAsNumber(minimumReceived, toToken?.denomination, toToken?.denomination),
    ]
  }, [fromValueNormalized, toValueNormalized, loadingOutput, slippage, toToken?.denomination])

  const [extraForm, setExtraForm] = useState<
    LimitOrderFormValues | DcaOrderFormValues | StopLosOrderFormValues | null
  >(null)

  // button validation
  const validationError = useMemo(() => {
    if (!fromToken) {
      return "Select a token"
    }
    if (!toToken) {
      return "Select a token"
    }
    if (fromTokenId === toTokenId) {
      return "Invalid pair"
    }
    if (!fromValueNormalized) {
      return "Enter an amount"
    }
    if (typeof fromBalance === "bigint" && fromValueNormalized > fromBalance) {
      return "Insufficient funds"
    }
    if (
      !isMultipleHops && // only check if it's not a multi-hop swap
      !loadingPool &&
      !loadingGlobalLiq &&
      !loadingReserves &&
      (!reserves || !reserves[fromToken.id] || !reserves[toToken.id])
    ) {
      return "Pool has no liquidity"
    }

    if (!loadingPool && !poolId && !loadingGlobalLiq && !isMultipleHops) {
      return "Pool not found"
    }

    if (orderType === "limit") {
      const { limitPrice } = (extraForm || {}) as LimitOrderFormValues
      if (isNaN(Number(limitPrice)) || Number(limitPrice) <= 0) return "Enter a limit price"
    }

    if (orderType === "dca") {
      const { runningTime, hours, minutes } = (extraForm || {}) as DcaOrderFormValues
      if (
        isNaN(Number(minutes)) ||
        isNaN(Number(hours)) ||
        Number(hours) < 0 ||
        Number(minutes) < 0
      )
        return "Running time is not valid"
      if (!runningTime) return "Enter running time"

      if (runningTime < 5 || runningTime > 7 * 24 * 60)
        return "Running time must be between 5min and 7days"
    }

    if (orderType === "stop-loss") {
      const { callback, sellPrice, stopLossPrice, variant } = (extraForm ||
        {}) as StopLosOrderFormValues

      if (Number(stopLossPrice) <= 0) return "Enter a stop loss price"
      if (variant === "limit" && Number(sellPrice) <= 0) return "Enter a sell price"
      if (variant === "trailing" && Number(callback) <= 0)
        return "Enter trailing stop loss percentage"
    }

    if (loadingOutput || loadingPool || loadingGlobalLiq) {
      return "Loading..."
    }

    if (orderType === "swap" && !isMultipleHops && receiveAtLeastNormalized === 0n) {
      return "Slippage too high"
    }

    if (isMultipleHops && isAgentLocked) {
      return "Your multi-hop agent is busy performing another swap"
    }

    return ""
  }, [
    fromToken,
    toToken,
    fromTokenId,
    toTokenId,
    fromValueNormalized,
    fromBalance,
    poolId,
    loadingPool,
    orderType,
    extraForm,
    reserves,
    loadingReserves,
    receiveAtLeastNormalized,
    loadingOutput,
    isAgentLocked,
    isMultipleHops,
    loadingGlobalLiq,
  ])

  // limit order output
  useEffect(() => {
    if (orderType !== "limit" || !toToken || !fromToken) return

    const { limitPrice } = (extraForm || {}) as Required<LimitOrderFormValues>
    if (isNaN(Number(limitPrice)) || Number(limitPrice) <= 0) {
      setToValue("")
      return
    }

    const limitPriceNormalized = parseNumberAsBigInt(limitPrice, toToken.denomination)
    const outputNormalized =
      (fromValueNormalized * limitPriceNormalized) / 10n ** BigInt(fromToken.denomination)
    const output = parseBigIntAsNumber(outputNormalized, toToken.denomination, toToken.denomination)
    setToValue(truncateNumberAuto(output))
  }, [extraForm, orderType, toToken, fromValueNormalized, fromToken])

  // stop loss order output
  useEffect(() => {
    if (orderType !== "stop-loss" || !toToken || !fromToken) return

    const { stopLossPrice, sellPrice, variant } = (extraForm ||
      {}) as Required<StopLosOrderFormValues>
    if (isNaN(Number(stopLossPrice)) || Number(stopLossPrice) <= 0) {
      setToValue("")
      return
    }

    if (variant === "limit" && (isNaN(Number(sellPrice)) || Number(sellPrice) <= 0)) {
      setToValue("")
      return
    }

    const stopLossPriceNormalized = parseNumberAsBigInt(
      variant === "limit" ? sellPrice : stopLossPrice,
      toToken.denomination,
    )

    const outputNormalized =
      (fromValueNormalized * stopLossPriceNormalized) / 10n ** BigInt(fromToken.denomination)
    const output = parseBigIntAsNumber(outputNormalized, toToken.denomination, toToken.denomination)
    setToValue(truncateNumberAuto(output))
  }, [extraForm, orderType, toToken, fromValueNormalized, fromToken])

  // confirm popover
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)

  function showPopover() {
    if (validationError) return
    setConfirmDialogOpen(true)
  }

  // cancel multi-hop swap
  const [cancelMultihopPopoverOpen, setCancelMultihopPopoverOpen] = useState(false)

  function closePopover() {
    if (orderStatus === "preparing") return

    if (orderStatus === "success" || orderStatus === "error" || orderStatus === "pending") {
      resetForm()
      enqueueSnackbar(
        <SwapOrderSnackbar
          poolId={poolId as string}
          orderType={orderType}
          orderExtra={extraForm}
          fromValue={fromValue}
          toValue={toValue}
          fromToken={fromToken as TokenInfo}
          toToken={toToken as TokenInfo}
          orderId={orderId as string}
          feePercentageBps={feePercentageBps}
          receiveAtLeast={receiveAtLeast}
          slippage={slippage}
          fee={fee}
          price={price as number}
          priceImpact={priceImpact}
          orderStatus={orderStatus}
          orderError={orderError}
          orderOutputAmount={orderOutputAmount}
          orderConfirmationId={orderConfirmationId}
        />,
        {
          persist: true,
          key: orderId,
        },
      )
    }

    resetState()
    setConfirmDialogOpen(false)
  }

  // sim
  const [simulationFailed, setSimulationFailed] = useState(true)
  const [simulationError, setSimulationError] = useState<string>()

  // order id
  const [orderId, setOrderId] = useState<string>()

  // order status
  const [orderStatus, setOrderStatus] = useState<OrderStatus>()

  // agent creation stage
  const [agentStage, setAgentStage] = useState<CreateAgentStage>("spawning")

  // order error
  const [orderError, setOrderError] = useState<string>()

  // swap result
  const [orderOutputAmount, setOrderOutputAmount] = useState<bigint>()
  const [orderConfirmationId, setOrderConfirmationId] = useState<string>()

  // execute swap
  async function doSwap(skipSimulation = false) {
    if (!!validationError || loadingOutput) return

    logger.log("Order type: ", orderType)
    logger.log("Order extra: ", extraForm)

    try {
      let orderId: string

      if (orderType === "swap") {
        if (isMultipleHops) {
          if (!simpleHopperAddress || !swapRoute) {
            throw new Error("Simple Hopper Agent not found")
          }

          setSimulationFailed(false)
          setSimulationError(undefined)
          if (!skipSimulation && showExperimentalFeatures) {
            setOrderStatus("pendingSimulation")
            try {
              const zeroAddress = "0000000000000000000000000000000000000000000"
              await simulateMultiHop(swapRoute, activeAddr || zeroAddress, slippage, ao)
            } catch (error) {
              logger.error("onSimulationError", error)
              setSimulationFailed(true)
              setSimulationError(String(error))
              return
            }
          }

          setOrderStatus("pendingWalletConfirmation")
          orderId = await swapMultiHop(
            {
              simpleHopperAddress,
              swapRoute: swapRoute,
              input: fromValueNormalized,
              token: fromTokenId as string,
            },
            ao,
          )
        } else {
          setOrderStatus("pendingWalletConfirmation")
          orderId = await swap(
            {
              input: fromValueNormalized,
              minOutput: receiveAtLeastNormalized,
              token: fromTokenId as string,
              pool: poolId as string,
            },
            ao,
          )
        }
      } else if (orderType === "limit") {
        setOrderStatus("preparing")
        setAgentStage("spawning")
        orderId = await limitOrder(
          {
            input: fromValueNormalized,
            output: toValueNormalized,
            fromTokenId: fromTokenId as string,
            toTokenId: toTokenId as string,
            pool: poolId as string,
            expiry: (extraForm as LimitOrderFormValues).expiry,
          },
          ao,
          () => {
            setAgentStage("top-up")
          },
        )
        setAgentStage("connecting")
        await wait(2000)
        setAgentStage("receiving")
        await wait(2000)
        setAgentStage("finalizing")
        await wait(2000)
      } else if (orderType === "dca") {
        setOrderStatus("preparing")
        setAgentStage("spawning")
        orderId = await dcaOrder(
          {
            input: fromValueNormalized,
            output: toValueNormalized,
            fromTokenId: fromTokenId as string,
            toTokenId: toTokenId as string,
            pool: poolId as string,
            runningTime: (extraForm as DcaOrderFormValues).runningTime,
          },
          ao,
          () => {
            setAgentStage("top-up")
          },
        )
        setAgentStage("connecting")
        await wait(2000)
        setAgentStage("receiving")
        await wait(2000)
        setAgentStage("finalizing")
        await wait(2000)
      } else if (orderType === "stop-loss") {
        setOrderStatus("preparing")
        setAgentStage("spawning")
        orderId = await stopLossOrder(
          {
            input: fromValueNormalized,
            output: toValueNormalized,
            fromTokenId: fromTokenId as string,
            toTokenId: toTokenId as string,
            pool: poolId as string,
            reserves: reserves as Reserves,
            fromTokenDenomination: fromToken?.denomination as number,
            toTokenDenomination: toToken?.denomination as number,
            ...(extraForm as StopLosOrderFormValues),
          },
          ao,
          () => {
            setAgentStage("top-up")
          },
        )
        setAgentStage("connecting")
        await wait(2000)
        setAgentStage("receiving")
        await wait(2000)
        setAgentStage("finalizing")
        await wait(2000)
      } else {
        throw new Error("Invalid order type")
      }

      logger.log("onOrderId", orderId)
      setOrderId(orderId)
      setOrderStatus("pending")
      refetchFromBalance()
      refetchToBalance()
    } catch (error) {
      setAgentStage("spawning")
      if (!(error instanceof Error)) {
        setOrderError(String(error))
        setOrderStatus("error")
        return
      }

      if (error.message.includes("User rejected")) {
        setOrderStatus(undefined)
        return
      }

      setOrderStatus("error")
      setOrderError(error.message)
    }
  }

  useEffect(() => {
    if (!orderId) return

    const abortController = new AbortController()
    ;(async function () {
      try {
        let tokensReceived = 0n
        let confirmationId = ""
        if (orderType === "swap") {
          ;[tokensReceived, confirmationId] = isMultipleHops
            ? await retryable(readSwapResultMultiHop)(orderId, toTokenId as string)
            : await retryable(readSwapResult)(orderId)
        }

        if (abortController.signal.aborted) return

        setOrderOutputAmount(tokensReceived)
        setOrderConfirmationId(confirmationId)
        setOrderStatus("success")
      } catch (error) {
        if (abortController.signal.aborted) return

        if (!(error instanceof Error)) {
          setOrderError(String(error))
          setOrderStatus("error")
          return
        }

        setOrderStatus("error")
        setOrderError(error.message)
      }
    })()

    return () => {
      abortController.abort()
    }
  }, [orderId, orderType])

  // reset inputs
  function resetForm() {
    setFromValue("")
    setToValue("")
  }

  // reset order state
  function resetState() {
    setOrderOutputAmount(undefined)
    setOrderConfirmationId(undefined)
    setOrderStatus(undefined)
    setOrderError(undefined)
    setOrderId(undefined)
    setAgentStage("spawning")
    setSimulationFailed(false)
    setSimulationError(undefined)
  }

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"))

  const deployAgentMutation = useDeployHopperAgent()

  const handleDeployAgent = async () => {
    try {
      const response = await deployAgentMutation.mutateAsync()

      logger.log("onDeployAgentSuccess", response)
    } catch (error) {
      logger.error("onDeployAgentError", error)
    }
  }

  // Use hopper to calculate approximate transaction cost
  const fromHopperPrice = useHopperPrice(fromTokenId, true)
  const toHopperPrice = useHopperPrice(toTokenId, true)

  const transactionCost = useMemo(() => {
    if (!fromHopperPrice.data || !toHopperPrice.data || !fromValue || !toValue) return 0

    const fromValueinUSD = Number(fromValue) * fromHopperPrice.data
    const toValueinUSD = Number(toValue) * toHopperPrice.data

    const cost = fromValueinUSD - toValueinUSD

    return cost
  }, [fromHopperPrice, toHopperPrice, fromValue, toValue])

  const [cancelHopperLoading, setCancelHopperLoading] = useState(false)

  const handleCancelHopperAgent = async () => {
    if (!simpleHopperAddress) return

    setCancelHopperLoading(true)
    try {
      await cancelHopperOrder(simpleHopperAddress, ao)
    } catch (error) {
      logger.error("onCancelHopperError", error)
    } finally {
      setCancelHopperLoading(false)
    }
  }

  return (
    <Page style={{ position: "relative" }}>
      <TitleBar
        variant="solid"
        rightActions={
          <Stack direction="row" gap={0.5} alignItems="center">
            {poolId && (
              <IconButton
                disabled={loadingOutput || loadingReserves}
                size="medium"
                className={css`
                  svg {
                    width: 16px;
                    height: 16px;
                  }
                `}
                onClick={() => {
                  if (!poolId) return

                  setRefresh(Math.random())
                  refetchReserves().then(({ data }) => {
                    logger.log("onReservesUpdate", data)
                  })
                }}
              >
                {" "}
                <RefreshCw01
                  style={{
                    animation:
                      loadingOutput || loadingReserves
                        ? "loading-rotate 0.57s ease-in-out infinite"
                        : "none",
                  }}
                />
              </IconButton>
            )}
            <Tooltip title={HELP_LINK_TOOLTIPS[orderType]}>
              <MuiLink
                target="_blank"
                href={HELP_LINKS[orderType]}
                underline="hover"
                variant="body2"
                sx={{ color: "var(--mui-palette-text-secondary)" }}
              >
                <IconButton size="medium">
                  <HelpCircle />
                </IconButton>
              </MuiLink>
            </Tooltip>
            {orderType === "limit" ? null : (
              <SettingsPopover slippage={slippage} setSlippage={setSlippage} />
            )}
          </Stack>
        }
        leftActions={
          <Tabs
            value={orderType}
            variant="scrollable"
            scrollButtons={false}
            onChange={(_event, newValue) => {
              searchParams.set("type", newValue)
              if (newValue === "swap") {
                searchParams.delete("type")
              }
              setSearchParams(searchParams, { replace: true })
              setExtraForm(null)
            }}
            sx={(theme) => ({
              marginY: 1,
              minHeight: "unset",
              width: "100%",
              [`& .${tabsClasses.indicator}`]: {
                background: "var(--mui-palette-background-paper)",
                border: "1px solid var(--mui-palette-borderPrimary)",
                borderRadius: 0.75,
                height: "100%",
              },
              [`& .${tabsClasses.flexContainer}`]: {
                gap: 0.25,
              },
              [`& .${tabClasses.selected}`]: {
                color: "var(--mui-palette-accent-main) !important",
              },
              [`& .${tabClasses.root}:hover`]: {
                color: "var(--mui-palette-accent-main)",
              },
              [`& .${tabClasses.root}`]: {
                lineHeight: 1,
                minHeight: "unset",
                paddingY: 1.25,
                transition: "none",
                zIndex: 2,
                [theme.breakpoints.down("md")]: {
                  minWidth: "unset",
                  paddingX: 0.75,
                },
              },
              [`& .${tabClasses.root} svg`]: {
                display: { xs: "none", sm: "inherit" },
              },
            })}
          >
            <Tab value={"swap" satisfies OrderType} label="Swap" />
            <Tab
              value={"limit" satisfies OrderType}
              label={
                <Stack direction="row" gap={1} alignItems="center">
                  <AgentSvg height={20} width={24} />
                  <span>Limit</span>
                </Stack>
              }
              component={motion.div}
              whileHover="hover"
              initial="rest"
              animate="rest"
            />
            <Tab
              value={"stop-loss" satisfies OrderType}
              label={
                <Stack direction="row" gap={1} alignItems="center">
                  <AgentSvg height={20} width={24} />
                  <span>Stop Loss</span>
                </Stack>
              }
              component={motion.div}
              whileHover="hover"
              initial="rest"
              animate="rest"
            />
            <Tab
              value={"dca" satisfies OrderType}
              label={
                <Stack direction="row" gap={1} alignItems="center">
                  <AgentSvg height={20} width={24} />
                  <span>DCA</span>
                </Stack>
              }
              component={motion.div}
              whileHover="hover"
              initial="rest"
              animate="rest"
            />
          </Tabs>
        }
      />
      <Stack
        gap={1}
        component={Paper}
        elevation={0}
        sx={{
          borderTopLeftRadius: "0",
          borderTopRightRadius: "0",
          padding: 1,
          width: "100%",
        }}
      >
        <Box
          sx={(theme) => ({
            background: "var(--mui-palette-background-default)",
            borderRadius: "var(--mui-shape-borderRadius)",
            [theme.breakpoints.up("md")]: {
              minWidth: "unset",
              display: "grid",
              gridTemplateColumns: "1fr auto 1fr",
              alignItems: "flex-start",
            },
            [theme.breakpoints.down("md")]: {
              display: "flex",
              flexDirection: "column",
            },
          })}
        >
          <TokenInput
            label={orderType === "dca" ? "I want to allocate" : "You Sell"}
            tokenID={fromTokenId}
            onTokenIdChange={(tokenId) => {
              setToValue("")
              if (!tokenId) searchParams.delete("from")
              else searchParams.set("from", tokenId)
              setSearchParams(searchParams, { replace: true })
            }}
            tokenInfo={fromToken}
            tokens={tokens}
            autoFocus
            onSubmit={showPopover}
            value={fromValue}
            onChange={(newValue, useMax) => {
              logger.log("onInputChange", {
                tokenId: fromTokenId,
                amount: newValue,
                useMax,
              })
              setFromValue(newValue)
            }}
            balance={fromBalance}
          />
          <Box
            sx={(theme) => ({
              display: "flex",
              height: "100%",
              width: "100%",
              position: "relative",
              alignItems: "center",
              justifyContent: "center",
              "& > button": {
                [theme.breakpoints.down("md")]: {
                  transform: "rotate(90deg) !important",
                },
              },
            })}
          >
            <Box
              sx={(theme) => ({
                backgroundColor: "var(--mui-palette-borderPrimary)",
                height: "100%",
                width: "1px",
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                [theme.breakpoints.down("md")]: {
                  width: "100%",
                  height: "1px",
                },
              })}
            />
            <IconButton
              className={css`
                background-color: var(--mui-palette-background-default);
              `}
              variant="outlined"
              size="medium"
              color="accent"
              onClick={(event) => {
                event.preventDefault()
                switchTokens()
              }}
            >
              <ArrowNarrowRight />
            </IconButton>
          </Box>
          {orderType === "dca" ? (
            <DcaOrderForm
              onValuesChange={setExtraForm}
              variant="alt"
              label="To buy"
              tokenID={toTokenId}
              onTokenIdChange={(tokenId) => {
                setToValue("")
                if (!tokenId) searchParams.delete("to")
                else searchParams.set("to", tokenId)
                setSearchParams(searchParams, { replace: true })
              }}
              tokenInfo={toToken}
              tokens={suggestedTokens}
              onSubmit={showPopover}
              loading={loadingOutput}
              readOnly
              value={null}
              balance={toBalance}
              showSuggestions
            />
          ) : (
            <TokenInput
              label="You Buy"
              tokenID={toTokenId}
              onTokenIdChange={(tokenId) => {
                setToValue("")
                if (!tokenId) searchParams.delete("to")
                else searchParams.set("to", tokenId)
                setSearchParams(searchParams, { replace: true })
              }}
              outputText={
                orderType === "stop-loss" &&
                (extraForm as StopLosOrderFormValues)?.variant !== "limit"
                  ? "at market price"
                  : undefined
              }
              tokenInfo={toToken}
              tokens={suggestedTokens}
              onSubmit={showPopover}
              loading={loadingOutput}
              readOnly
              value={toValue}
              balance={toBalance}
              showSuggestions
            />
          )}
        </Box>
        {orderType === "limit" && (
          <LimitOrderForm
            fromToken={fromToken}
            toToken={toToken}
            onValuesChange={setExtraForm}
            reserves={reserves}
            poolId={poolId}
          />
        )}
        {orderType === "stop-loss" && (
          <StopLossOrderForm
            onValuesChange={setExtraForm}
            toToken={toToken}
            fromToken={fromToken}
            reserves={reserves}
          />
        )}
        <Dialog
          fullScreen={isMobile}
          fullWidth
          maxWidth="md"
          position="top"
          sx={{
            "& .MuiDialog-paper": {
              minHeight: 450,
              justifyContent: "center",
            },
          }}
          open={confirmDialogOpen}
          onClose={closePopover}
        >
          <IconButton
            onClick={closePopover}
            size="large"
            style={{
              position: "absolute",
              top: "20px",
              right: "20px",
              zIndex: 1,
            }}
          >
            <XClose />
          </IconButton>
          {orderStatus === "preparing" ? (
            <Stack alignItems="center">
              <CreateAgentAnimation stage={agentStage} />
            </Stack>
          ) : orderStatus === "pending" ? (
            <SwapOrderPendingForm
              fromValue={fromValue}
              toValue={toValue}
              fromToken={fromToken as TokenInfo}
              toToken={toToken as TokenInfo}
              orderType={orderType}
              orderExtra={extraForm}
              multiHop={
                swapRoute && swapRoute.dijkstra_route.pool_addresses.length > 1
                  ? swapRoute.dijkstra_route
                  : undefined
              }
            />
          ) : orderStatus === "success" ? (
            <SwapOrderSuccessForm
              poolId={poolId as string}
              orderType={orderType}
              fromValue={fromValue}
              toValue={toValue}
              orderOutputAmount={orderOutputAmount as bigint}
              orderConfirmationId={orderConfirmationId as string}
              fromToken={fromToken as TokenInfo}
              toToken={toToken as TokenInfo}
              orderId={orderId as string}
              orderExtra={extraForm}
            />
          ) : (
            <Stack gap={2}>
              <SwapOrderPreviewForm
                orderType={orderType}
                orderExtra={extraForm}
                fromValue={fromValue}
                toValue={toValue}
                fromToken={fromToken as TokenInfo}
                toToken={toToken as TokenInfo}
              />
              <div>
                <SwapOrderDetails
                  orderType={orderType}
                  orderExtra={extraForm}
                  feePercentageBps={feePercentageBps}
                  receiveAtLeast={receiveAtLeast}
                  slippage={slippage}
                  fromToken={fromToken as TokenInfo}
                  toToken={toToken as TokenInfo}
                  price={price}
                  priceImpact={priceImpact}
                  fee={fee}
                  multiHop={
                    swapRoute && swapRoute.dijkstra_route.pool_addresses.length > 1
                      ? swapRoute.dijkstra_route
                      : undefined
                  }
                />
              </div>
              {orderError && (
                <Alert type="error" topSpace>
                  {orderError}
                </Alert>
              )}
              {orderStatus === "pendingSimulation" && (
                <>
                  {simulationError && (
                    <Alert type="error">Simulation failed: {simulationError}</Alert>
                  )}
                  <Stack direction={"row"} gap={2}>
                    <ContinueButton
                      className="accent"
                      onClick={() => doSwap()}
                      disabled={!simulationFailed}
                    >
                      {simulationFailed ? "Retry" : "Simulating..."}
                    </ContinueButton>
                    {simulationFailed && (
                      <ContinueButton className="outlined" onClick={() => doSwap(true)}>
                        Swap anyway
                      </ContinueButton>
                    )}
                  </Stack>
                </>
              )}
              {!orderStatus && (
                <ContinueButton onClick={() => doSwap()} className="accent">
                  {BUTTON_LABELS[orderType]}
                </ContinueButton>
              )}
              {orderStatus === "pendingWalletConfirmation" && (
                <ContinueButton disabled className="secondary">
                  Confirm Transaction in your Wallet
                </ContinueButton>
              )}
              {orderStatus === "error" && (
                <ContinueButton onClick={closePopover} className="secondary">
                  Close
                </ContinueButton>
              )}
            </Stack>
          )}
        </Dialog>
        {!activeAddr ? (
          <ContinueButton onClick={connect} className="accent">
            Connect Wallet
            <CardholderIcon />
          </ContinueButton>
        ) : validationError === "Pool not found" ? (
          <ContinueButton
            className="accent"
            onClick={() => {
              navigate(`/add?tokenA=${fromTokenId}&tokenB=${toTokenId}`)
            }}
          >
            Create Liquidity Pool
          </ContinueButton>
        ) : validationError === "Pool has no liquidity" ? (
          <ContinueButton
            className="accent"
            onClick={() => {
              navigate(`/add?tokenA=${fromTokenId}&tokenB=${toTokenId}`)
            }}
          >
            Add to Liquidity Pool
          </ContinueButton>
        ) : isMultipleHops ? (
          isAgentStuck ? (
            <ContinueButton
              onClick={() => {
                setCancelMultihopPopoverOpen(true)
              }}
              className={"accent"}
              style={{
                background: "rgba(var(--mui-palette-warning-mainChannel) / 0.75) !important",
              }}
            >
              Cancel Ongoing Swap
            </ContinueButton>
          ) : (
            <ContinueButton
              onClick={!simpleHopperAddress ? handleDeployAgent : showPopover}
              disabled={!!validationError || deployAgentMutation.isPending || orderType !== "swap"}
              className={
                !validationError && !deployAgentMutation.isPending && orderType === "swap"
                  ? "accent"
                  : "secondary"
              }
            >
              {validationError && validationError.includes("agent is busy") && (
                <CircularProgress
                  size={24}
                  sx={{
                    "& svg": {
                      width: "24px",
                      height: "24px",
                      fontSize: "24px",
                    },
                  }}
                />
              )}
              <Stack alignItems="center">
                {orderType !== "swap" ? (
                  "Coming soon for multi-hops"
                ) : !simpleHopperAddress ? (
                  <>
                    Create an Smart Trade Agent
                    <Box sx={{ fontSize: "1.05rem" }}>to continue with the Swap</Box>
                  </>
                ) : (
                  validationError || "Swap using Agent"
                )}

                {deployAgentMutation.isPending ? (
                  <Box sx={{ fontSize: "1.05rem" }}>
                    <CircularProgress size={24} /> Deploying Agent
                  </Box>
                ) : null}
              </Stack>
            </ContinueButton>
          )
        ) : (
          <ContinueButton
            onClick={showPopover}
            disabled={!!validationError}
            className={!validationError ? "accent" : "secondary"}
          >
            {validationError || BUTTON_LABELS[orderType]}
          </ContinueButton>
        )}
      </Stack>
      {isMultipleHops ? (
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            marginTop: "var(--spacing)",
            marginBottom: "var(--spacing)",
            background: "transparent",
          }}
        >
          <Stack direction={"row"} gap={4}>
            {swapRoute && <SwapMultiHopRoute route={swapRoute?.dijkstra_route} />}

            <Stack gap={1} mt={2} mb={4} sx={{ padding: "var(--spacing)", flex: 2, minWidth: 300 }}>
              {simpleHopperAddress ? (
                <Typography variant="body2" style={{ textAlign: "right" }}>
                  <InfoCircle style={{ width: 16, height: 16, minWidth: 16, marginRight: 4 }} />
                  Your swap will be executed via your personal Autonomous Routing Agent, which is
                  actively identifying the most efficient route for your transaction.
                </Typography>
              ) : (
                <Stack gap={1}>
                  <Typography sx={{ fontWeight: 600 }}>
                    This swap does not have a direct AMM pair.
                  </Typography>
                  <Typography variant="body2">
                    We will deploy our Autonomous Routing Agent to find and execute the most
                    efficient route across the ecosystem for you.
                    <br />
                    This free, one-time setup establishes your personal agent, which will
                    automatically handle future trade optimizations on Botega.
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Stack>
          {price !== undefined && toToken && fromToken && (
            <>
              <TitleBar
                className={css`
                  margin-top: calc(var(--spacing) * 1.25);
                `}
                leftActions={
                  price === null ? (
                    <Skeleton width={160} />
                  ) : (
                    <PriceLabel
                      onClick={() => setPriceForAsset(priceForAsset === "from" ? "to" : "from")}
                    >
                      {priceForAsset === "from" ? (
                        <>
                          1 {formatTicker(fromToken.ticker)} = {formatNumberAuto(price)}{" "}
                          {formatTicker(toToken.ticker)}
                        </>
                      ) : (
                        <>
                          1 {formatTicker(toToken.ticker)} = {formatNumberAuto(price)}{" "}
                          {formatTicker(fromToken.ticker)}
                        </>
                      )}
                    </PriceLabel>
                  )
                }
                rightActions={
                  <IconButton
                    size="medium"
                    onClick={() => {
                      setDetailsDropdownOpen((prev) => !prev)
                    }}
                    style={{
                      transform: detailsDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                    }}
                  >
                    <ChevronDown />
                  </IconButton>
                }
              />
              <Details>
                <Collapse in={detailsDropdownOpen}>
                  <MultiHopOrderDetails
                    orderType={orderType}
                    orderExtra={extraForm}
                    feePercentageBps={feePercentageBps}
                    receiveAtLeast={receiveAtLeast}
                    slippage={slippage}
                    fromToken={fromToken as TokenInfo}
                    toToken={toToken as TokenInfo}
                    priceImpact={priceImpact}
                    fee={fee}
                    transactionCost={transactionCost}
                  />
                </Collapse>
              </Details>
            </>
          )}
        </Paper>
      ) : (
        price !== undefined &&
        toToken &&
        fromToken && (
          <>
            <TitleBar
              className={css`
                margin-top: calc(var(--spacing) * 1.25);
              `}
              leftActions={
                price === null ? (
                  <Skeleton width={160} />
                ) : (
                  <PriceLabel
                    onClick={() => setPriceForAsset(priceForAsset === "from" ? "to" : "from")}
                  >
                    {priceForAsset === "from" ? (
                      <>
                        1 {formatTicker(fromToken.ticker)} = {formatNumberAuto(price)}{" "}
                        {formatTicker(toToken.ticker)}
                      </>
                    ) : (
                      <>
                        1 {formatTicker(toToken.ticker)} = {formatNumberAuto(price)}{" "}
                        {formatTicker(fromToken.ticker)}
                      </>
                    )}
                  </PriceLabel>
                )
              }
              rightActions={
                <IconButton
                  size="medium"
                  onClick={() => {
                    setDetailsDropdownOpen((prev) => !prev)
                  }}
                  style={{
                    transform: detailsDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                >
                  <ChevronDown />
                </IconButton>
              }
            />
            <Details>
              <Collapse in={detailsDropdownOpen}>
                <SwapOrderDetails
                  orderType={orderType}
                  orderExtra={extraForm}
                  feePercentageBps={feePercentageBps}
                  receiveAtLeast={receiveAtLeast}
                  slippage={slippage}
                  fromToken={fromToken as TokenInfo}
                  toToken={toToken as TokenInfo}
                  priceImpact={priceImpact}
                  fee={fee}
                />
              </Collapse>
            </Details>
          </>
        )
      )}
      <Dialog
        fullScreen={isMobile}
        fullWidth
        open={cancelMultihopPopoverOpen}
        onClose={() => setCancelMultihopPopoverOpen(false)}
        maxWidth="sm"
        position="top"
        sx={{
          "& .MuiDialog-paper": {
            minHeight: 450,
            justifyContent: "center",
          },
        }}
      >
        <IconButton
          onClick={closePopover}
          size="large"
          style={{
            position: "absolute",
            top: "20px",
            right: "20px",
            zIndex: 1,
          }}
        >
          <XClose />
        </IconButton>
        <Typography variant="h5" fontFamily={TitleFont}>
          Emergency Cancel Swap
        </Typography>
        <Stack sx={{ paddingX: 0 }} component={DialogContent} gap={2}>
          <Typography variant="body2" color="textSecondary">
            Your agent appears to be stuck. Canceling now will refund you the agent's existing
            balances. This cancellation will reset the state, allowing you to initiate a new
            multi-hop swap.
          </Typography>
          {simpleAgent?.agent && simpleHopperAddress && (
            <>
              <div>
                <Typography variant="subtitle1" fontWeight={700}>
                  Path
                </Typography>
                <Stack direction="row" gap={1}>
                  {agentPaths.map((path) => (
                    <>
                      <TokenDetails key={path} token={path} />
                      {path !== agentPaths[agentPaths.length - 1] && <span>→</span>}
                    </>
                  ))}
                </Stack>
              </div>
              <div>
                <Typography variant="subtitle1" fontWeight={700}>
                  Agent Balances
                </Typography>
                <Stack paddingX={1}>
                  {agentPaths.map((path) => (
                    <>
                      <TokenBalance key={path} token={path} owner={simpleHopperAddress} />
                    </>
                  ))}
                </Stack>
              </div>
            </>
          )}
        </Stack>
        <DialogActions>
          <ContinueButton
            onClick={handleCancelHopperAgent}
            disabled={cancelHopperLoading}
            className={"accent"}
            style={{
              background: "rgba(var(--mui-palette-warning-mainChannel) / 0.75) !important",
            }}
          >
            {cancelHopperLoading ? "Cancelling Ongoing Swap..." : "Cancel Ongoing Swap"}
          </ContinueButton>
        </DialogActions>
      </Dialog>
    </Page>
  )
}

const Page = styled.div`
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const Details = styled(Stack)`
  gap: 0;
  width: 100%;
  padding: 0 calc(var(--spacing) * 1.25);
  box-sizing: border-box;
`
const ContinueButton = styled(Button)`
  padding: 1.4rem 1.55rem;
  width: 100%;
  font-size: 1.35rem;
`

const PriceLabel = styled(Button)`
  padding: 0;
  font-weight: 700;
  text-align: start;
`

export function TokenBalance({ token, owner }: { token: string; owner: string }) {
  const [tokenInfo] = useTokenInfo(token)

  const ao = useAo()

  const { data: balance } = useQuery({
    queryKey: [`balance_${token}`, owner],
    enabled: !!token && !!owner,
    queryFn: async () => {
      if (!owner) throw new Error("No active address")
      if (!token) throw new Error("No from token id")

      return getBalance(token, owner, ao).catch(() => 0n)
    },
  })

  return (
    <li>
      <Typography
        variant="body2"
        component={Stack}
        direction="row"
        alignItems="center"
        gap={1}
        sx={{ display: "inline-flex" }}
      >
        <span>{tokenInfo?.ticker}</span>

        {balance === undefined ? (
          <Skeleton width={80} />
        ) : (
          <Typography variant="inherit" fontFamily={NumberFont}>
            {formatNumberAuto(parseBigIntAsNumber(balance, tokenInfo?.denomination))}
          </Typography>
        )}
      </Typography>
    </li>
  )
}
